'use client'

import type { FormEvent, ReactNode } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Eye,
  Globe,
  Loader2,
  Lock,
  Play,
  Search,
  Sparkles,
} from 'lucide-react'
import { Button, Input, Select } from '@/components/platform/ui'
import { LOCALES } from '@/lib/radar/locales'
import type { AuditEntity } from '@/lib/radar/entity-types'
import { TOOLS } from '@/types/radar'
import type { ToolKey } from '@/types/radar'
import { Chip, InstrumentLabel, Panel } from './ui'

/**
 * The "Run new audit" screen.
 *
 * Presentational ONLY. Every value and every handler is owned by
 * app/(platform)/platform/app/page.tsx — this component renders the approved
 * design (public/Radar New Audit 2.html) over the existing production form.
 * There is no second submit path, no local copy of the domain, and no mock
 * behaviour: `onSubmit` is the real `handleRun`, `onReset` the real
 * `handleNewTest`.
 *
 * The reference shows the completed-audit happy path only, so three states it
 * does not depict are extended here in the same language: validation errors,
 * the in-flight progress button, and the free-tier tool lock.
 */

const EXAMPLE_DOMAINS = [
  'stripe.com',
  'vercel.com',
  'linear.app',
  'notion.so',
  'figma.com',
]

export interface NewAuditViewProps {
  domain: string
  onDomainChange: (value: string) => void
  /**
   * Runs brand detection. Takes the domain explicitly because the example
   * buttons fire it in the same tick as `onDomainChange`, before React has
   * re-rendered with the new value — the parent's closure would still hold
   * the previous domain. Omit the argument for a real blur, where component
   * state is already current.
   */
  onDomainBlur: (domain?: string) => void
  brand: string
  onBrandChange: (value: string) => void
  category: string
  onCategoryChange: (value: string) => void
  locale: string
  onLocaleChange: (value: string) => void
  brandDetected: boolean
  detectingBrand: boolean
  brandDescription: string
  /** Provenance of the resolved entity. Null before any domain is resolved. */
  entity: AuditEntity | null
  /** True when a run was blocked pending confirmation of the entity. */
  entityConfirmPending: boolean
  /** "Yes, that's us" — makes the resolved entity authoritative. */
  onConfirmEntity: () => void
  inputError: string | null
  selected: Set<ToolKey>
  onToggleTool: (key: ToolKey) => void
  advancedOpen: boolean
  onAdvancedOpenChange: (open: boolean) => void
  customizeOpen: boolean
  onCustomizeOpenChange: (open: boolean) => void
  isRunning: boolean
  doneCount: number
  scanUsed: boolean
  userTier: 'free' | 'paid'
  onUpgradeClick: () => void
  onSubmit: (e: FormEvent) => void
  /** Undefined when there is no audit to go back to (first-run). */
  onBack?: () => void
  /** Clears the current audit and the form. The real `handleNewTest`. */
  onReset?: () => void
}

export function NewAuditView({
  domain,
  onDomainChange,
  onDomainBlur,
  brand,
  onBrandChange,
  category,
  onCategoryChange,
  locale,
  onLocaleChange,
  brandDetected,
  detectingBrand,
  brandDescription,
  entity,
  entityConfirmPending,
  onConfirmEntity,
  inputError,
  selected,
  onToggleTool,
  advancedOpen,
  onAdvancedOpenChange,
  customizeOpen,
  onCustomizeOpenChange,
  isRunning,
  doneCount,
  scanUsed,
  userTier,
  onUpgradeClick,
  onSubmit,
  onBack,
  onReset,
}: NewAuditViewProps) {
  const lockedOut = scanUsed && userTier === 'free'

  return (
    <>
      <Panel gradientRule as='section' aria-label='Start a new audit'>
        <form onSubmit={onSubmit} data-audit-form>
          <div className='flex flex-col gap-5 px-6 py-7 sm:px-8'>
            <div className='flex flex-col gap-1.5'>
              <span className='font-display text-[25px] font-semibold leading-snug tracking-[-0.02em]'>
                Grade a domain
              </span>
              <span className='block text-sm text-muted-foreground'>
                13 tools run in staged batches. One unified report in a few
                minutes.
              </span>
            </div>

            <div className='flex flex-col items-stretch gap-3 sm:flex-row sm:items-end'>
              <Input
                label='Domain'
                data-tour='domain'
                type='text'
                inputSize='lg'
                leadingIcon={Search}
                loading={detectingBrand}
                value={domain}
                onChange={e => onDomainChange(e.target.value)}
                // Wrapped, not passed by reference: the handler's first
                // parameter is now a domain string, and forwarding the React
                // FocusEvent into it would hand detection an event object.
                onBlur={() => onDomainBlur()}
                placeholder='Enter domain to audit (e.g. stripe.com)'
                disabled={isRunning}
                containerClassName='flex-1'
                aria-invalid={inputError ? true : undefined}
                aria-describedby={inputError ? 'audit-domain-error' : undefined}
              />
              {lockedOut ? (
                <Button
                  type='button'
                  variant='cta'
                  size='lg'
                  leadingIcon={Sparkles}
                  onClick={onUpgradeClick}
                >
                  Unlock unlimited scans
                </Button>
              ) : (
                <RunButton
                  isRunning={isRunning}
                  doneCount={doneCount}
                  total={selected.size}
                />
              )}
            </div>

            {inputError && (
              <span
                id='audit-domain-error'
                role='alert'
                className='block border px-3 py-2 text-xs'
                style={{
                  borderColor:
                    'color-mix(in srgb, var(--cta) 40%, transparent)',
                  color: 'var(--cta)',
                }}
              >
                {inputError}
              </span>
            )}

            {brandDetected && brandDescription && (
              <span className='flex items-start gap-2 text-xs text-muted-foreground'>
                <Sparkles
                  size={12}
                  aria-hidden='true'
                  className='mt-0.5 shrink-0'
                  style={{ color: 'var(--warning-text)' }}
                />
                {brandDescription}
              </span>
            )}

            <div className='flex flex-wrap items-center gap-2.5'>
              <InstrumentLabel tone='muted'>Try an example</InstrumentLabel>
              {EXAMPLE_DOMAINS.map(d => (
                <button
                  key={d}
                  type='button'
                  disabled={isRunning}
                  onClick={() => {
                    onDomainChange(d)
                    // Blur handler does detection; fire it directly so a
                    // click-to-fill behaves the same as typing then tabbing.
                    // `d` is passed explicitly: the state set above has not
                    // been applied yet, so a no-arg call would detect the
                    // PREVIOUS domain's brand.
                    onDomainBlur(d)
                  }}
                  className='border border-hairline px-2.5 py-1 font-mono text-xs text-muted-foreground transition-colors hover:border-hairline-strong hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:opacity-50'
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Entity confirmation gate. Shown when a run was blocked because the
              company could not be resolved from the site with high confidence.
              The audit is not cheap and its output is not harmless, so an
              unverified entity asks rather than proceeds. */}
          {entityConfirmPending && (
            <div className='border border-hairline-strong bg-muted/40 p-4 flex flex-col gap-3'>
              <div className='flex items-center gap-2'>
                <AlertTriangle size={14} className='shrink-0 text-warning' />
                <span className='text-sm font-semibold text-foreground leading-none'>
                  Confirm the company before running
                </span>
              </div>
              <p className='text-xs text-muted-foreground'>
                {entity?.conflicts.length
                  ? `This site gives conflicting signals about who it belongs to: ${entity.conflicts[0].values[0]} vs ${entity.conflicts[0].values[1]}.`
                  : entity?.source === 'domain_fallback'
                    ? 'We could not read an organization name from this site, so this is a guess from the domain.'
                    : 'We could not confirm this name from the site with enough certainty to audit against it.'}{' '}
                Auditing the wrong company produces results for someone else.
              </p>
              <div className='flex flex-col gap-1'>
                <span className='text-[11px] font-bold uppercase tracking-widest text-muted-foreground leading-none'>
                  We resolved
                </span>
                <span className='text-sm font-semibold text-foreground'>
                  {brand || '(none)'}
                </span>
                {entity && (
                  <span className='text-[11px] text-muted-foreground'>
                    {entity.evidence.length > 0
                      ? entity.evidence
                          .map(e => `${e.source}: ${e.value}`)
                          .join(' · ')
                      : 'no first-party evidence found'}
                  </span>
                )}
              </div>
              <div className='flex flex-wrap items-center gap-2'>
                <Button type='button' size='sm' onClick={onConfirmEntity}>
                  Yes, that&apos;s the company
                </Button>
                <span className='text-xs text-muted-foreground'>
                  or correct the brand name below, then run again.
                </span>
              </div>
            </div>
          )}

          <DisclosureRow
            label='Advanced options'
            hint={brandDetected ? 'brand auto-detected' : undefined}
            open={advancedOpen}
            onOpenChange={onAdvancedOpenChange}
          >
            <div className='flex flex-col gap-3'>
              <div className='flex flex-col gap-3 sm:flex-row'>
                <Input
                  label='Brand name'
                  type='text'
                  inputSize='md'
                  value={brand}
                  onChange={e => onBrandChange(e.target.value)}
                  placeholder={detectingBrand ? 'Resolving…' : 'Brand name'}
                  // The tick used to appear for ANY auto-detected value, which
                  // read as "confirmed" when it was in fact a guess — the
                  // reason a technically sophisticated user let a wrong entity
                  // through. It now means verified-or-confirmed, and an
                  // unverified value is marked as such.
                  trailingIcon={
                    !brand ? undefined : entity &&
                      (entity.confirmedByUser ||
                        entity.confidence === 'high') ? (
                      <CheckCircle2
                        size={12}
                        className='shrink-0 text-growth'
                      />
                    ) : brandDetected ? (
                      <AlertTriangle
                        size={12}
                        className='shrink-0 text-warning'
                      />
                    ) : undefined
                  }
                  disabled={isRunning}
                  containerClassName='flex-1'
                />
                <Input
                  label='Industry / category'
                  type='text'
                  inputSize='md'
                  value={category}
                  onChange={e => onCategoryChange(e.target.value)}
                  placeholder={
                    detectingBrand ? 'Detecting…' : 'Industry / category'
                  }
                  trailingIcon={
                    brandDetected && category ? (
                      <CheckCircle2
                        size={12}
                        className='shrink-0 text-growth'
                      />
                    ) : undefined
                  }
                  disabled={isRunning}
                  containerClassName='flex-1'
                />
              </div>
              <Select
                label='Audit locale'
                inputSize='md'
                leadingIcon={Globe}
                value={locale}
                onChange={e => onLocaleChange(e.target.value)}
                disabled={isRunning}
                containerClassName='sm:max-w-[240px]'
              >
                {LOCALES.map(l => (
                  <option key={l.code} value={l.code}>
                    {l.flag} {l.name}
                  </option>
                ))}
              </Select>
            </div>
          </DisclosureRow>

          <DisclosureRow
            label={`Customize tools · ${selected.size} of ${TOOLS.length} selected`}
            open={customizeOpen}
            onOpenChange={onCustomizeOpenChange}
          >
            <div className='flex flex-col gap-4'>
              {userTier === 'free' && (
                <div className='flex flex-col gap-3 border border-purple/30 bg-surface-inset p-3 sm:flex-row sm:items-center'>
                  <Lock
                    size={16}
                    aria-hidden='true'
                    className='shrink-0'
                    style={{ color: 'var(--purple)' }}
                  />
                  <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                    <span className='text-xs font-semibold'>
                      6 more tools locked — unlock for the full picture
                    </span>
                    <span className='block text-[11px] leading-relaxed text-muted-foreground'>
                      Citation Tracker (4 LLMs + Grok), Reddit Monitor,
                      Hallucination Detection, Prompt SOV, Source Influence Map,
                      Answer Engine Tester. From $5 per audit.
                    </span>
                  </div>
                  <Button
                    type='button'
                    variant='cta'
                    size='sm'
                    onClick={onUpgradeClick}
                  >
                    Unlock
                  </Button>
                </div>
              )}
              <div className='flex flex-wrap gap-2'>
                {TOOLS.map(t => {
                  const on = selected.has(t.key)
                  const isLocked = userTier === 'free' && t.tier > 1
                  return (
                    <button
                      key={t.key}
                      type='button'
                      onClick={() =>
                        isLocked ? onUpgradeClick() : onToggleTool(t.key)
                      }
                      disabled={isRunning}
                      aria-pressed={isLocked ? undefined : on}
                      aria-disabled={isRunning || isLocked || undefined}
                      // A locked chip renders only the tool name, so `title`
                      // alone would leave a screen-reader user with no idea
                      // why pressing it opens a paywall. Name it explicitly.
                      aria-label={
                        isLocked ? `${t.name} — paid plan required` : undefined
                      }
                      title={
                        isLocked ? `${t.name} — paid plan required` : t.name
                      }
                      className='inline-flex items-center gap-1.5 border px-2.5 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:cursor-not-allowed'
                      style={{
                        borderColor:
                          !isLocked && on
                            ? `color-mix(in srgb, ${t.color} 45%, transparent)`
                            : 'var(--pm-hairline)',
                        color: isLocked
                          ? 'var(--pm-text-muted)'
                          : on
                            ? t.color
                            : 'var(--muted-foreground)',
                        opacity: isRunning ? 0.4 : isLocked ? 0.55 : 1,
                      }}
                    >
                      {isLocked ? <Lock size={12} /> : null}
                      {t.name}
                      {!isLocked && on ? <CheckCircle2 size={12} /> : null}
                    </button>
                  )
                })}
              </div>
            </div>
          </DisclosureRow>
        </form>

        {(onBack || onReset) && (
          <div className='flex flex-wrap items-center justify-between gap-3 border-t border-hairline px-6 py-3.5 sm:px-8'>
            <span className='block text-xs text-instrument'>
              {onBack
                ? 'Your current report stays available until a new audit finishes.'
                : 'Runs all selected tools in one pass.'}
            </span>
            {onReset && (
              <button
                type='button'
                onClick={onReset}
                className='text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card'
              >
                Clear current audit
              </button>
            )}
          </div>
        )}
      </Panel>

      <AuditPreviewCard />
      <HowItWorks />
      <ToolInventory />
    </>
  )
}

/**
 * Submit control. Doubles as the progress indicator during a run — the fill
 * width tracks completed tools, which is why it is not a plain <Button>.
 */
function RunButton({
  isRunning,
  doneCount,
  total,
}: {
  isRunning: boolean
  doneCount: number
  total: number
}) {
  return (
    <button
      type='submit'
      data-tour='run'
      disabled={isRunning}
      aria-disabled={isRunning}
      aria-live={isRunning ? 'polite' : undefined}
      className='relative flex h-11 shrink-0 items-center justify-center gap-2 overflow-hidden px-6 text-sm font-semibold text-white transition-colors hover:bg-[#c3086e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:cursor-wait'
      style={{
        background: 'var(--cta-fill)',
        minWidth: isRunning ? 200 : 130,
      }}
    >
      {isRunning ? (
        <>
          <span
            aria-hidden='true'
            className='absolute inset-y-0 left-0 bg-black/20 transition-all duration-500 ease-out'
            style={{ width: `${total > 0 ? (doneCount / total) * 100 : 0}%` }}
          />
          <Loader2 size={14} className='relative z-10 animate-spin' />
          <span className='relative z-10'>
            Scanning {doneCount}/{total}
          </span>
        </>
      ) : (
        <>
          <Play size={14} aria-hidden='true' />
          Run audit
        </>
      )}
    </button>
  )
}

/**
 * Full-width disclosure row, styled as a footer band of the card. Uses a real
 * <details>/<summary> so it is keyboard-operable and announced as expandable
 * without any JS.
 */
function DisclosureRow({
  label,
  hint,
  open,
  onOpenChange,
  children,
}: {
  label: string
  hint?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}) {
  return (
    <details
      open={open}
      onToggle={e => onOpenChange((e.target as HTMLDetailsElement).open)}
      className='group/row border-t border-hairline'
    >
      <summary className='flex cursor-pointer select-none items-center justify-between gap-3 px-6 py-3.5 transition-colors hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset sm:px-8'>
        <InstrumentLabel tone='secondary'>
          {label}
          {hint && (
            <span className='ml-2 normal-case tracking-normal text-instrument'>
              · {hint}
            </span>
          )}
        </InstrumentLabel>
        <ChevronDown
          size={14}
          aria-hidden='true'
          className='shrink-0 -rotate-90 text-muted-foreground transition-transform group-open/row:rotate-0'
        />
      </summary>
      <div className='border-t border-hairline px-6 py-5 sm:px-8'>
        {children}
      </div>
    </details>
  )
}

/**
 * Static illustration of the report a run produces. Deliberately NOT wired to
 * live data — it is what the customer gets, shown before they spend a credit.
 * The figures are the reference's own sample values and are labelled as a
 * preview of example.com, never presented as the viewer's own results.
 */
function AuditPreviewCard() {
  const tiles = [
    {
      label: 'Unified score',
      value: '74',
      color: 'var(--pm-grade-b)',
      note: 'Grade B, across 13 tools',
    },
    {
      label: 'Crawl integrity',
      value: '88',
      color: 'var(--pm-grade-a)',
      note: 'Bots 92 · Robots 88 · llms 84',
    },
    {
      label: 'Action plan',
      value: '7',
      note: 'Fixes ready, ranked by priority',
    },
    {
      label: 'Issues',
      value: '6',
      color: 'var(--cta)',
      note: '2 errors · 1 warning · 3 passed',
    },
  ]

  return (
    <Panel as='section' aria-label='Report preview'>
      <div className='flex flex-wrap items-center gap-2.5 border-b border-hairline px-6 py-3.5 sm:px-8'>
        <Eye size={14} aria-hidden='true' className='shrink-0 text-primary' />
        <InstrumentLabel className='!text-[var(--primary)]'>
          Preview
        </InstrumentLabel>
        <span className='text-[13px] text-muted-foreground'>
          What your dashboard will look like after the first audit
        </span>
      </div>

      <div className='flex flex-col gap-4 px-6 py-6 sm:px-8'>
        <div className='flex flex-wrap items-center gap-3'>
          <span className='text-sm font-semibold'>example.com</span>
          <span className='font-display text-xl font-semibold'>
            74<span className='text-sm text-instrument'>/100</span>
          </span>
          <Chip color='var(--pm-grade-b)'>B</Chip>
          <span className='flex-1' />
          <span className='font-mono text-[11px] text-instrument'>
            Save · PDF · Share
          </span>
        </div>

        <div
          className='flex items-start gap-3 border bg-surface-inset px-4 py-3'
          style={{
            borderColor:
              'color-mix(in srgb, var(--warning-text) 40%, transparent)',
          }}
        >
          <Chip color='var(--warning-text)' className='mt-px'>
            Conflict
          </Chip>
          <span className='text-[13px] leading-relaxed text-muted-foreground'>
            Crawl access strong (92) but llms.txt is missing key entity
            definitions. Bots reach the site, but can&apos;t map the brand.
          </span>
        </div>

        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4'>
          {tiles.map(t => (
            <div
              key={t.label}
              className='flex flex-col gap-2 border border-hairline bg-surface-inset p-4'
            >
              <InstrumentLabel tone='muted'>{t.label}</InstrumentLabel>
              <span
                className='font-display text-[31px] font-semibold leading-none'
                style={t.color ? { color: t.color } : undefined}
              >
                {t.value}
              </span>
              <span className='text-xs text-muted-foreground'>{t.note}</span>
            </div>
          ))}
        </div>

        <div className='flex flex-wrap justify-between gap-x-4 gap-y-1 pt-1 font-mono text-[11px] text-instrument'>
          <span>Pass rate 10/13</span>
          <span>Total time 24.6s</span>
          <span>13/13 completed · 0 failed</span>
        </div>
      </div>
    </Panel>
  )
}

function HowItWorks() {
  const steps = [
    {
      n: '1',
      color: 'var(--primary)',
      title: 'Enter your domain',
      body: 'Radar auto-detects your brand name and industry. Select which tools to run or keep all 13 enabled.',
    },
    {
      n: '2',
      color: 'var(--purple)',
      title: 'Tools run in staged batches',
      body: 'Lightweight checks run together while provider-intensive checks are isolated. Each tool analyzes a different dimension of AI visibility.',
    },
    {
      n: '3',
      color: 'var(--growth)',
      title: 'Get actionable insights',
      body: 'Cross-tool analysis reveals conflicts and opportunities. Prioritized action items tell you exactly what to fix, why, and how.',
    },
  ]

  return (
    <Panel as='section' aria-label='How the audit works' className='p-6 sm:p-8'>
      <div className='flex flex-col gap-7'>
        <h2 className='text-center font-display text-[25px] font-semibold tracking-[-0.02em]'>
          How the audit works
        </h2>
        <div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
          {steps.map(s => (
            <div
              key={s.n}
              className='flex flex-col items-center gap-3 text-center'
            >
              <span
                aria-hidden='true'
                className='flex h-10 w-10 shrink-0 items-center justify-center border font-mono text-base'
                style={{
                  borderColor: `color-mix(in srgb, ${s.color} 50%, transparent)`,
                  color: s.color,
                }}
              >
                {s.n}
              </span>
              <span className='text-base font-semibold'>{s.title}</span>
              <span className='text-pretty text-sm leading-relaxed text-muted-foreground'>
                {s.body}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  )
}

function ToolInventory() {
  return (
    <Panel
      as='section'
      aria-label='Tools included'
      className='flex flex-col gap-5 px-6 py-7 sm:px-8'
    >
      <InstrumentLabel tone='secondary'>
        13 tools, one unified report
      </InstrumentLabel>
      <div className='grid grid-cols-1 gap-x-6 sm:grid-cols-2 lg:grid-cols-3'>
        {TOOLS.map(t => (
          <div
            key={t.key}
            className='flex items-baseline justify-between gap-2 border-b border-hairline py-2.5'
          >
            <span className='text-sm'>{t.name}</span>
            <span className='font-mono text-[11px] text-instrument'>
              ~{t.estSeconds}s
            </span>
          </div>
        ))}
      </div>
    </Panel>
  )
}
